/**
 * PATH: apps/hr/src/pages/documents/document-detail/DocumentSidebar.tsx
 * PURPOSE: Right sidebar with document info, linked employee, tags, and OCR confidence
 */

import { Link } from 'react-router-dom'
import { FileText, Building, Calendar, Plus, X } from 'lucide-react'
import { cn, formatDate } from '../../../lib/utils'
import { TagBadge } from './DetailBadges'
import type { Document } from '../../../types'

interface DocumentSidebarProps {
  document: Document & { documentTypeName?: string; issuer?: string }
  isExpired: boolean
  isExpiringSoon: boolean
  appliedTags: string[]
  suggestedTags: string[]
  availableTags: { name: string; usedByCount: number }[]
  tagSearch: string
  showTagDropdown: boolean
  onTagSearchChange: (v: string) => void
  onShowTagDropdown: (v: boolean) => void
  onApplyTag: (tag: string) => void
  onRemoveTag: (tag: string) => void
}

export function DocumentSidebar({
  document, isExpired, isExpiringSoon,
  appliedTags, suggestedTags, availableTags,
  tagSearch, showTagDropdown, onTagSearchChange,
  onShowTagDropdown, onApplyTag, onRemoveTag,
}: DocumentSidebarProps) {
  return (
    <div className="space-y-4">
      {/* Document Info */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Document Info</h3>
        <div className="space-y-4">
          <InfoRow icon={FileText} label="Document Type"
            value={(document as Record<string, unknown>).documentTypeName as string || document.documentType || 'Unknown'} />
          {document.issuer && <InfoRow icon={Building} label="Issuer" value={document.issuer} />}
          {document.expiryDate && (
            <div className="flex items-center gap-3">
              <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center',
                isExpired ? 'bg-red-100 dark:bg-red-900/30' : isExpiringSoon ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-slate-100 dark:bg-slate-800')}>
                <Calendar className={cn('w-4 h-4', isExpired ? 'text-red-500' : isExpiringSoon ? 'text-amber-500' : 'text-slate-500')} />
              </div>
              <div>
                <p className="text-xs text-slate-500">Expiry Date</p>
                <p className={cn('text-sm font-medium', isExpired ? 'text-red-600' : isExpiringSoon ? 'text-amber-600' : 'text-slate-900 dark:text-white')}>
                  {formatDate(document.expiryDate)}
                  {document.daysUntilExpiry !== undefined && (
                    <span className="text-xs ml-1">
                      {document.daysUntilExpiry < 0 ? `(Expired ${Math.abs(document.daysUntilExpiry)} days ago)` :
                       document.daysUntilExpiry === 0 ? '(Expires today)' : `(${document.daysUntilExpiry} days)`}
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Linked Employee */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Linked Employee</h3>
        <Link to={`/employees/${document.employeeId}`}
          className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white text-sm font-medium">
            {document.employeeName.split(' ').map(n => n[0]).join('')}
          </div>
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-white">{document.employeeName}</p>
            <p className="text-xs text-teal-600 dark:text-teal-400">View profile →</p>
          </div>
        </Link>
      </div>

      {/* Tags */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Tags</h3>
        <div className="space-y-4">
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Applied Tags</p>
            <div className="flex flex-wrap gap-2">
              {appliedTags.length > 0 ? appliedTags.map(tag => (
                <TagBadge key={tag} tag={tag} removable onRemove={() => onRemoveTag(tag)} />
              )) : <p className="text-sm text-slate-400 italic">No tags applied</p>}
            </div>
          </div>

          {suggestedTags.filter(tag => !appliedTags.includes(tag)).length > 0 && (
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                AI Suggested Tags <span className="text-xs ml-1 text-slate-400">(click to apply)</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {suggestedTags.filter(tag => !appliedTags.includes(tag)).map(tag => (
                  <button key={tag} onClick={() => onApplyTag(tag)}
                    className="px-2.5 py-1 text-xs font-medium rounded-full border-2 border-dashed border-amber-400 dark:border-amber-500/50 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:border-amber-500 transition-colors">
                    + {tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Add Tag</p>
            <div className="relative">
              <input type="text" placeholder="Search or create tag..." value={tagSearch}
                onChange={(e) => { onTagSearchChange(e.target.value); onShowTagDropdown(true) }}
                onFocus={() => onShowTagDropdown(true)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              {showTagDropdown && (
                <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {availableTags
                    .filter(t => t.name.toLowerCase().includes(tagSearch.toLowerCase()) && !appliedTags.includes(t.name))
                    .slice(0, 8)
                    .map(tag => (
                      <button key={tag.name} onClick={() => onApplyTag(tag.name)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-between group">
                        <span className="text-slate-700 dark:text-slate-300">{tag.name}</span>
                        <span className="text-xs text-slate-400 group-hover:text-teal-500">{tag.usedByCount} uses</span>
                      </button>
                    ))}
                  {tagSearch.trim() && !availableTags.some(t => t.name.toLowerCase() === tagSearch.toLowerCase()) && (
                    <button onClick={() => onApplyTag(tagSearch.trim())}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-teal-50 dark:hover:bg-teal-900/20 border-t border-slate-100 dark:border-slate-700 flex items-center gap-2 text-teal-600 dark:text-teal-400">
                      <Plus className="w-4 h-4" /><span>Create & apply "{tagSearch.trim()}"</span>
                    </button>
                  )}
                  <button onClick={() => { onShowTagDropdown(false); onTagSearchChange('') }}
                    className="w-full px-3 py-1.5 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 border-t border-slate-100 dark:border-slate-700">
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* OCR Confidence */}
      {document.confidenceScore !== undefined && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3">OCR Confidence</h3>
          <div className="relative h-4 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden mb-2">
            <div className={cn('h-full rounded-full transition-all',
              document.confidenceScore >= 90 ? 'bg-emerald-500' : document.confidenceScore >= 70 ? 'bg-amber-500' : 'bg-red-500')}
              style={{ width: `${document.confidenceScore}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className={cn('font-medium',
              document.confidenceScore >= 90 ? 'text-emerald-600' : document.confidenceScore >= 70 ? 'text-amber-600' : 'text-red-600')}>
              {document.confidenceScore}%
            </span>
            <span className="text-slate-500">
              {document.confidenceScore >= 90 ? 'High' : document.confidenceScore >= 70 ? 'Medium' : 'Low'}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

function InfoRow({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
        <Icon className="w-4 h-4 text-slate-500" />
      </div>
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-sm font-medium text-slate-900 dark:text-white">{value}</p>
      </div>
    </div>
  )
}
